// Loads the JSON content into typed registries. All balance/content lives in the
// JSON (GDD §8.4); this module is the single typed door onto it.

import factionsRaw from "../data/factions.json";
import legionRaw from "../data/legion.json";
import kindledRaw from "../data/kindled.json";
import mapRaw from "../data/map_ruined_streets.json";
import type { Faction, MapDef, UnitDef } from "./types";

export interface FactionMeta {
  id: Faction;
  name: string;
  blurb: string;
  hook: string;
  heroId: string;
  colors: {
    primary: string;
    secondary: string;
    steel: string;
    trim: string;
    banner: string;
  };
}

const legionUnits = legionRaw as unknown as UnitDef[];
const kindledUnits = kindledRaw as unknown as UnitDef[];

export const FACTIONS: Record<Faction, FactionMeta> = factionsRaw as unknown as Record<
  Faction,
  FactionMeta
>;

export const UNITS: Record<string, UnitDef> = {};
for (const u of [...legionUnits, ...kindledUnits]) UNITS[u.id] = u;

export function unitsOf(faction: Faction): UnitDef[] {
  return faction === "legion" ? legionUnits : kindledUnits;
}

export function unit(id: string): UnitDef {
  const u = UNITS[id];
  if (!u) throw new Error(`unknown unit id: ${id}`);
  return u;
}

export const BATTLE_MAP: MapDef = mapRaw as unknown as MapDef;

export function heroId(faction: Faction): string {
  return FACTIONS[faction].heroId;
}

/** Buildable structures (data kept inline — small, fixed set per GDD §4.7). */
export interface StructureDef {
  kind: "barracks" | "generator" | "turret";
  name: string;
  cost: { req: number; pwr: number };
  buildTime: number;
  hp: number;
  radius: number;
}

export const STRUCTURES: Record<string, StructureDef> = {
  barracks: { kind: "barracks", name: "Barracks", cost: { req: 120, pwr: 0 }, buildTime: 20, hp: 900, radius: 34 },
  generator: { kind: "generator", name: "Generator", cost: { req: 90, pwr: 0 }, buildTime: 16, hp: 600, radius: 24 },
  turret: { kind: "turret", name: "Base Turret", cost: { req: 90, pwr: 30 }, buildTime: 18, hp: 700, radius: 20 },
};

/** Cost to advance the HQ from T1 → T2 (gates assault/vehicle/upgrades). */
export const TIER2_COST = { req: 200, pwr: 40 };
export const TIER2_TIME = 30;
